import { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastItemProps extends Toast {
  onRemove: (id: string) => void;
}

const icons = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const styles = {
  success: 'bg-white dark:bg-charcoal-800 border-success-500/50 text-success-400',
  error: 'bg-white dark:bg-charcoal-800 border-danger-500/50 text-danger-400',
  warning: 'bg-white dark:bg-charcoal-800 border-amber-450/50 text-amber-450',
  info: 'bg-white dark:bg-charcoal-800 border-info-500/50 text-info-400',
};

function ToastItem({ id, type, title, message, duration = 5000, onRemove }: ToastItemProps) {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const Icon = icons[type];

  useEffect(() => {
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      
      if (remaining === 0) {
        clearInterval(timer);
        handleClose();
      }
    }, 50);

    return () => clearInterval(timer);
  }, [duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onRemove(id), 300);
  };

  return (
    <div
      className={`
        relative overflow-hidden rounded-xl border shadow-2xl backdrop-blur-sm
        min-w-[320px] max-w-[420px] p-4
        animate-slide-up transition-all duration-300
        ${isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'}
        ${styles[type]}
      `}
    >
      {/* Progress bar */}
      <div 
        className="absolute bottom-0 left-0 h-0.5 bg-current opacity-30 transition-all duration-100"
        style={{ width: `${progress}%` }}
      />
      
      <div className="flex items-start gap-3">
        <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 dark:text-warm-cream">{title}</p>
          {message && (
            <p className="text-sm text-gray-600 dark:text-warm-sand mt-1 leading-relaxed">{message}</p>
          )}
        </div>
        <button
          onClick={handleClose}
          className="flex-shrink-0 p-1 hover:bg-white/5 rounded-lg transition-colors"
        >
          <X className="w-4 h-4 text-gray-600 dark:text-warm-sand" />
        </button>
      </div>
    </div>
  );
}

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

export function ToastContainer({ toasts, onRemove }: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-3">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} {...toast} onRemove={onRemove} />
      ))}
    </div>
  );
}
